import React, { useState, useEffect, createContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import DraggableThemeButton from './components/ui/DraggableThemeButton';
import { LockProvider, useLock } from './context/LockContext'; 
import { useIdleLock } from './hooks/useIdleLock'; 
import LockScreenModal from './components/LockScreen';
import { getScreenKeyFromPath } from './config/screens';
import { activityService } from './api/authService';
import './pages/VendorManager.css';
import { AiJobsProvider } from './context/AiJobsContext';

// 🚀 1. استيراد الصفحات الأساسية فورياً
const LandingPage = lazy(() => import('./pages/LandingPage'));
const RegisterForm = lazy(() => import('./pages/register'));
const Dashboard = lazy(() => import('./pages/dashboard'));

// 💤 2. تطبيق التحميل الكسول (Lazy Loading) لبقية صفحات التطبيق
const Gate = lazy(() => import('./pages/Gate'));
const AddMember = lazy(() => import('./pages/AddMember'));
const VendorManager = lazy(() => import('./pages/VendorManager'));
const ActivateAccount = lazy(() => import('./pages/ActivateAccount'));
const BankManager = lazy(() => import('./pages/BankManager'));
const VendorsDashboard = lazy(() => import('./pages/VendorsDashboard'));
const VendorDetails = lazy(() => import('./pages/VendorDetails'));
const AddContainer = lazy(() => import('./pages/AddContainer'));
const InvoiceDetails = lazy(() => import('./pages/InvoiceDetails'));
const PIInvoiceEntry = lazy(() => import('./pages/PIInvoiceEntry'));
const VendorsList = lazy(() => import('./pages/VendorsList'));
const TransferRequests = lazy(() => import('./pages/TransferRequests'));
const SwiftEnter = lazy(() => import('./pages/SwiftEnter'));
const PriorStatement = lazy(() => import('./pages/PriorStatement'));
const CiInvoice = lazy(() => import('./pages/CiInvoice'));
const VendorsSummary = lazy(() => import('./pages/VendorsSummary'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const TransferReport = lazy(() => import('./pages/TransferReport'));
const VendorInvoices = lazy(() => import('./pages/VendorInvoices'));
const Settings = lazy(() => import('./pages/Settings'));
const TrackingWindow = lazy(() => import('./pages/TrackingWindow'));
const ManageMembers = lazy(() => import('./pages/ManageMembers'));
const AdminApprovalScreen = lazy(() => import('./pages/AdminApprovalScreen'));
const CompanyArchive = lazy(() => import('./pages/CompanyArchive'));

export const ThemeContext = createContext();

// 🆕 مفتاح مؤقت لتمرير رسالة "لا تملك صلاحية" بين شاشة الوصول المرفوض والشاشة التي رجعنا إليها
const ACCESS_DENIED_KEY = 'access-denied-toast-message';

// 🆕 إشعار عائم يظهر أعلى الشاشة عند رفض الوصول، ثم يختفي تلقائياً
const AccessDeniedToast = () => {
  const [message, setMessage] = useState(null);
  const location = useLocation();

  // كل ما تتغير الصفحة (يعني رجعنا لمكان ما)، نفحص هل فيه رسالة رفض صلاحية بانتظارنا
  useEffect(() => {
    const stored = sessionStorage.getItem(ACCESS_DENIED_KEY);
    if (stored) {
      setMessage(stored);
      sessionStorage.removeItem(ACCESS_DENIED_KEY);
    }
  }, [location.pathname]);

  // إخفاء الإشعار تلقائياً بعد 3.5 ثانية
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 3500);
    return () => clearTimeout(timer);
  }, [message]);

  if (!message) return null;

  return (
    <div
      onClick={() => setMessage(null)}
      style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#ef4444',
        color: '#fff',
        padding: '14px 24px',
        borderRadius: '14px',
        fontWeight: '700',
        fontSize: '14px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.28)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        maxWidth: '90vw',
        textAlign: 'center',
      }}
    >
      🚫 {message}
    </div>
  );
};

const ProtectedRoute = ({ screen, children }) => {
  const navigate = useNavigate();
  const permissions = JSON.parse(localStorage.getItem('user_permissions') || '{}');
  const isOwner = localStorage.getItem('is_owner') === 'true';

  const requiredScreens = Array.isArray(screen) ? screen : [screen];

  const checkSinglePermission = (screenName) => {
    const userAccess = permissions[screenName];
    if (!userAccess) return false;

    if (Array.isArray(userAccess)) {
      return userAccess.includes('view') || userAccess.includes('VIEW');
    }
    return Boolean(userAccess);
  };

  const hasAccess = isOwner || requiredScreens.every(checkSinglePermission);

  // 🆕 بدل التوجيه القسري لـ /dashboard: نعرض إشعار ونرجع المستخدم للصفحة التي كان فيها
  useEffect(() => {
    if (!hasAccess) {
      sessionStorage.setItem(ACCESS_DENIED_KEY, 'ليس لديك صلاحية للوصول إلى هذه الشاشة');
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  if (!hasAccess) {
    // لا نعرض أي محتوى أثناء الرجوع للخلف (لحظة واحدة فقط)
    return null;
  }

  return children;
};

const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80vh',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#38bdf8'
  }}>
    جاري تحميل الصفحة... ⏳
  </div>
);

// 🔒 المسارات العامة التي لا تتطلب تسجيل دخول
const PUBLIC_PATHS = [
  '/',
  '/auth',
  '/activate-account',
  '/reset-password',
  '/master-panel-hidden/gate',
  '/master-panel-hidden',
];

let lastLoggedPath = null;
let lastLoggedAt = 0;
const DUPLICATE_WINDOW_MS = 1500;

const usePageViewTracking = (isPublicPage) => {
  const location = useLocation();

  useEffect(() => {
    if (isPublicPage) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const screenKey = getScreenKeyFromPath(location.pathname);
    if (!screenKey) return;

    const now = Date.now();
    if (lastLoggedPath === location.pathname && (now - lastLoggedAt) < DUPLICATE_WINDOW_MS) {
      return;
    }
    lastLoggedPath = location.pathname;
    lastLoggedAt = now;

    activityService.createActivityLog({
      screen_name: screenKey,
      action_type: 'VIEW',
    }).catch((err) => {
      console.warn('تعذر تسجيل زيارة الشاشة:', err);
    });
  }, [location.pathname, isPublicPage]);
};

// 📦 مكون يعرض نافذة القفل شرط أن تكون الحالة locked وأن يكون المستخدم مسجلاً
function ConditionalLockModal() {
  const { isLocked } = useLock();
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  
  // لا تظهر الشاشة إذا لم يكن هناك توكن محلي بالأساس
  if (!isLocked || !token) return null;
  return <LockScreenModal />;
}

function AppContent() {
  const location = useLocation();
  const isPublicPage = PUBLIC_PATHS.includes(location.pathname);
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');

  // 🟢 الحل الأساسي: تعطيل المؤقت تماماً في الصفحات العامة أو عند عدم وجود توكن
  useIdleLock({
    idleLimitMs: 5 * 60 * 1000,
    enabled: !isPublicPage && Boolean(token) // 👈 تفعيل فقط في الصفحات الخاصة + مع وجود توكن
  });

  usePageViewTracking(isPublicPage);

  return (
    <>
      {/* 🆕 إشعار عدم الصلاحية يعيش هنا حتى يبقى ظاهراً عبر كل الصفحات */}
      <AccessDeniedToast />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} /> 
          <Route path="/auth" element={<RegisterForm />} /> 
          <Route path="/activate-account" element={<ActivateAccount />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/master-panel-hidden/gate" element={<Gate />} />
          <Route path="/master-panel-hidden" element={<AdminApprovalScreen />} />
          
          <Route path="/manage-members" element={
            <ProtectedRoute screen="users_admin">
              <ManageMembers />
            </ProtectedRoute>
          } />
          <Route path="/add-member" element={
            <ProtectedRoute screen="users_admin">
              <AddMember />
            </ProtectedRoute>
          } />

          <Route path="/vendor-management" element={
            <ProtectedRoute screen="vendors entry">
              <VendorManager />
            </ProtectedRoute>
          } />
          <Route path="/VendorsDashboard" element={
            <ProtectedRoute screen="dashboard">
              <VendorsDashboard />
            </ProtectedRoute>
          } />
          <Route path="/vendor-details/:id" element={
            <ProtectedRoute screen={["vendors summary", "vendor details"]}>
              <VendorDetails />
            </ProtectedRoute>
          } />

          <Route path="/vendor-summary/:id" element={
            <ProtectedRoute screen="vendors summary">
              <VendorsSummary />
            </ProtectedRoute>
          } />
          <Route path="/vendor-invoices/:id" element={
            <ProtectedRoute screen="vendor-invoice">
              <VendorInvoices />
            </ProtectedRoute>
          } />

          <Route path="/dashboard/banks" element={
            <ProtectedRoute screen="bank list">
              <BankManager />
            </ProtectedRoute>
          } />
          <Route path="/transfer-requests" element={
            <ProtectedRoute screen={["vendors summary", "vendor details", "transfer request"]}>
              <TransferRequests /> 
            </ProtectedRoute>
          } />
          <Route path="/swift-enter" element={
            <ProtectedRoute screen="banks">
              <SwiftEnter />
            </ProtectedRoute>
          } />

          <Route 
            path="/import" 
            element={
              <ProtectedRoute screen="import"> 
                <AddContainer />
              </ProtectedRoute>
            } 
          />
          <Route path="/ci-entry" element={
            <ProtectedRoute screen="CI invoices">
              <CiInvoice />
            </ProtectedRoute>
          } />
          <Route path="/pi-invoice-entry" element={
            <ProtectedRoute screen="PI invoices">
              <PIInvoiceEntry />
            </ProtectedRoute>
          } />

          <Route 
            path="/invoice-details/:containerId/:vendorId/:invoiceId?" 
            element={
              <ProtectedRoute screen="create invoice"> 
                <InvoiceDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vendor-list/:containerId" 
            element={
              <ProtectedRoute screen="vendorlist">
                <VendorsList />
              </ProtectedRoute>
            } 
          />

          <Route path="/archive" element={
            <ProtectedRoute screen="digital archive">
              <CompanyArchive />
            </ProtectedRoute>
          } />

          <Route path="/prior-statement" element={
            <ProtectedRoute screen="prior statement">
              <PriorStatement />
            </ProtectedRoute>
          } />
          <Route path="/transfer-report" element={
            <ProtectedRoute screen="report">
              <TransferReport />
            </ProtectedRoute>
          } />

          <Route path="/tracking" element={
            <ProtectedRoute screen="track">
              <TrackingWindow />
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('theme') === 'dark'
  );

  useEffect(() => {
    const root = window.document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const designSystem = {
    borderRadius: '12px',
    inputRadius: '6px',
    paddingTable: '10px 16px',
    fontSize: '13px',
    transition: 'all 0.2s ease-in-out'
  };

  const appStyles = {
    background: darkMode 
      ? `linear-gradient(135deg, #0b0f19 0%, #111827 50%, #1f2937 100%)` 
      : `linear-gradient(135deg, #002561 0%, #001233 50%, #0c1e43 100%)`, 
    minHeight: '100vh',
    color: '#ffffff',
    fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
    transition: 'all 0.3s ease-in-out'
  };

  return (
    <AiJobsProvider>                     
      <LanguageProvider> 
        <ThemeContext.Provider value={{ darkMode, designSystem }}>
          <Router> 
            <LockProvider>
              <div style={appStyles}>
                <DraggableThemeButton darkMode={darkMode} setDarkMode={setDarkMode} />
                
                <AppContent />

                {/* 🔒 عرض شاشة القفل عند الحاجة فقط */}
                <ConditionalLockModal />
              </div>
            </LockProvider>
          </Router>
        </ThemeContext.Provider>
      </LanguageProvider> 
    </AiJobsProvider>
  );
}

export default App;